/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { Field } from '@src/app/shared/field/Field';
import { ContactTypes, MAIL_STATUS, Person } from '@src/app/model/person';
import { PersonUnitRole } from '@src/app/model/person-unit-role';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FormConfig } from '@src/app/shared/form/FormConfig';
import { GridField } from '@src/app/shared/grid/grid-field';
import { STD_ANIMATION } from '@src/app/shared/std-animation';
import { PersonService } from '@src/app/pages/person-page/person.service';
import { FormEmailComponent } from '@src/app/shared/form/form-email/form-email.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { maxGridRows, required, uniqueGrid } from '@src/app/shared/validators';
import { of } from 'rxjs';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { Role } from '@src/app/model/role';
import { RoleService } from '@src/app/pages/role-page/role.service';
import { UpdateEmailAction } from '@src/app/pages/person-page/update-email-action';
import { RetireAction } from './retire-action';
import { FormPhoneComponent } from '@src/app/shared/form/form-phone/form-phone.component';
import { AgentContactPageNavRoute } from '@src/app/routes/agent-contact-page-nav';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DialogOptions } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { SupplierService } from '@src/app/modules/supply/supplier.service';
import { uuid } from '@src/app/model/abstract-object';

@Component({
    selector: 'app-agent-contact-page',
    template: '<app-form-page [config]="config" [dialogRef]="dialogRef"></app-form-page>',
    animations: [STD_ANIMATION],
    imports: [FormPageComponent]
})
export class AgentContactPageComponent extends AbstractPageComponent {
    dataSvc = inject(PersonService);
    roleService = inject(RoleService);
    supplierService = inject(SupplierService);
    dialogRef = inject<MatDialogRef<AgentContactPageComponent>>(MatDialogRef, { optional: true });
    dialogOptions = inject<DialogOptions>(MAT_DIALOG_DATA, { optional: true });

    help = $localize`
        Agent contacts are the people who work for your agency and need access to the system.
        Create a contact record for each team member, then assign them a role to grant them login access.
        Each contact should be assigned exactly one role.
    `;

    unclaimedHint = "When a user signs up with this email address, " +
        "they will be automatically matched to this contact and granted access";

    claimedHint = "This contact is tied to a verified user account — the email address can no longer be changed";

    emailField = FormEmailComponent.make('Email', 'email', { formColumn: 2, hint: this.unclaimedHint });

    firstNameField = FormTextComponent.make('First Name', 'firstName', { formColumn: 1 });
    lastNameField = FormTextComponent.make('Last Name', 'lastName', { formColumn: 1 });

    roleList: Role[] = [];

    roleGrid: GridField = new GridField({
        field: { value: 'roles', cellOpts: { heading: 'Roles' }, formColumn: 4, sendServer: false, visible: Field.formOnly },
        rowFactory: this.roleGridRow.bind(this),
        gridValidator: [uniqueGrid('roleId'), maxGridRows(1, 'roleId')],
        objFactory: () => of(new PersonUnitRole()),
    });

    personUnitRoleField = FieldMaker.idHolder('personUnits');

    supplierField = FormPicklistComponent.make('Supplier', 'supplierId', 'supplier', { service: this.supplierService }, { formColumn: 2 });

    fields = [
        FormTextComponent.make('Keyword Search', 'keyword', { visible: Field.noShow, sendServer: false }),
        FieldMaker.id(),
        FieldMaker.rev(),
        this.firstNameField,
        this.lastNameField,
        FormPhoneComponent.make('Telephone', 'phone', { formColumn: 2 }),
        this.emailField,
        FormPicklistComponent.make('Mail Status', 'mailStatus', null, { items: MAIL_STATUS.items }, { formColumn: 2 }),
        FormTextComponent.make('Entity Type', 'entityType', { visible: Field.noShow }),
        FormPicklistComponent.make('Contact Type', 'contactType', null, { items: ContactTypes.agentSelectables() }, { formColumn: 2 }),
        this.supplierField,
        FormTextComponent.make('User Name', 'user.name', { disable: true, sendServer: false, formColumn: 3, cellOpts: { width: '10%' } }),
        FieldMaker.notes({ formColumn: 3 }),
        this.roleGrid,
        this.personUnitRoleField,
        FieldMaker.idHolder('mergeId'),
    ];

    config = new FormConfig({
        navRoute: AgentContactPageNavRoute,
        docPath: 'omc-professional/agent-contacts',
        title: $localize`Contact`,
        help: this.help,
        fieldSet: new FieldSet({
            fields: this.fields,
            formLayout: [
                { cells: [{ width: '25%' }, { width: '25%' }, { width: '25%' }, { width: '25%' }] },
            ],
        }),
        service: this.dataSvc,
        mode: 'list',
        objectFactory: this.newEntry.bind(this),
        beforeEdit: this.beforeEdit.bind(this),
        beforeSave: this.beforeSave.bind(this),
        allowDelete: false, // Agency staff are retired, never deleted - see RetireAction
        actions: [new UpdateEmailAction(), new RetireAction()],
        setTeam: (teamId) => {
            this.roleService.get(false, null, teamId).subscribe(roles => {
                this.roleList.length = 0;
                roles.forEach((r: Role) => this.roleList.push(r));
            });
        }
    });

    roleGridRow(o: PersonUnitRole) {
        const f = FormPicklistComponent.make('Role', 'roleId', 'role', { items: [] }, { validators: [required] });

        if (o && o.id) {
            f.override({ readonly: true });
            f.setPicklistItems(this.roleList);
        } else if (o) {
            f.setPicklistItems(this.roleList.filter(r => !r.unitLinked));
        }

        return [FieldMaker.idHolder('id'), f, FieldMaker.deleteGridRow()];
    }

    newEntry() {
        this.emailField.defaultReadonly = false;
        this.emailField.hint = this.unclaimedHint;
        const p = new Person();
        p.entityType = 'ind';
        p.contactType = ContactTypes.agent;
        const supplierId = (this.dialogOptions as unknown as { supplierId?: uuid })?.supplierId;
        if (supplierId) {
            p.supplierId = supplierId;
            p.contactType = ContactTypes.supplier;
            this.supplierField.readonly = true;
        } else {
            this.supplierField.readonly = false;
        }
        return of(p);
    }

    beforeEdit(o: Person) {
        if (!o) o = new Person();
        if (o.userId) {
            this.emailField.hint = this.claimedHint;
            this.emailField.defaultReadonly = true;
        } else {
            this.emailField.hint = this.unclaimedHint;
            this.emailField.defaultReadonly = false;
        }
        o.roles = o.personUnits;
        this.supplierField.readonly = true;
        return o;
    }

    beforeSave() {
        this.personUnitRoleField.control.setValue(this.roleGrid.getFormValue());
    }
}
